// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * A part declared scrapped, scanned in again (§8.7).
 *
 * When an archive is scrapped, each serialized part is recorded as scrapped or restocked.
 * A restocked part is a good part and goes in like any other. A scrapped one should not be
 * on a machine at all, so entering its serial stops the operator with a dialog — the inline
 * note alone is easy to miss mid-machine with a scanner in hand. They either take it out
 * again or confirm they mean it (the label was reused, the scrap was a mistake): the server
 * does not refuse it, since the record of the scrap cannot be corrected.
 *
 * Asked once per serial per form: confirmed, it is not asked about again; taken out and
 * scanned again, it is.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { UsedSerial } from "@/api/types";
import { isUsableSerial, normalizeSerial } from "@/scanner/serials";
import type { SerialStatus } from "@/scanner/useSerialStatus";
import { Modal } from "./Modal";

/** The serials among these whose part was scrapped with an archive, in entry order. */
export function scrappedSerials(serials: string[], status: SerialStatus): UsedSerial[] {
  const seen = new Set<string>();
  const found: UsedSerial[] = [];
  for (const raw of serials) {
    if (!isUsableSerial(raw)) continue;
    const serial = normalizeSerial(raw);
    const hit = status.used.get(serial);
    if (seen.has(serial) || hit?.scrap_outcome !== "SCRAPPED") continue;
    seen.add(serial);
    found.push(hit);
  }
  return found;
}

export function ScrappedSerialPrompt({
  serials,
  status,
  onRemove,
}: {
  /** Every serial entered on the form, as typed. */
  serials: string[];
  status: SerialStatus;
  /** Take a serial (normalized) out of every box it was entered in. */
  onRemove: (serial: string) => void;
}) {
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState<ReadonlySet<string>>(new Set());
  const hit = scrappedSerials(serials, status).find(
    (candidate) => !confirmed.has(candidate.serial_normalized),
  );
  if (!hit) return null;

  const keep = () => setConfirmed(new Set([...confirmed, hit.serial_normalized]));
  const remove = () => onRemove(hit.serial_normalized);

  return (
    <Modal
      title={t("scrappedSerial.title")}
      onClose={remove}
      footer={
        <>
          {/* Taking it out is the safe answer, and where focus starts: a second scan
              arriving while this is open must not confirm a scrapped part. */}
          <button type="button" className="button-primary" data-autofocus onClick={remove}>
            {t("scrappedSerial.remove")}
          </button>
          <button type="button" className="button-danger" onClick={keep}>
            {t("scrappedSerial.keep")}
          </button>
        </>
      }
    >
      <div className="banner banner-error" role="alert">
        {t("scrappedSerial.body", {
          serial: hit.serial_raw || hit.serial_normalized,
          archiveNo: hit.archive_no,
          position: hit.position_name,
          date: new Date(hit.effective_at).toLocaleDateString(),
        })}
      </div>
      <p>{t("scrappedSerial.question")}</p>
    </Modal>
  );
}
